import ChevronBackCircleOutline from '@components/icons/ChevronBackCircleOutline/ChevronBackCircleOutline'
import ChevronForwardCircleOutline from '@components/icons/ChevronForwardCircleOutline/ChevronForwardCircleOutline'
import ProductCard from '@components/ui/ProductCard/ProductCard'
import { ProductGridTileFragment } from '@lib/graphql/graphql'
import useRecommendations, {
  EventType,
  ServingConfig,
} from '@lib/hooks/useRecommendations'
import { isMobile, isTablet } from '@utils/helpers/DeviceUtils'
import clsx from 'clsx'
import { CSSProperties, FC, useState } from 'react'
import { FreeMode, Keyboard, Mousewheel, Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import s from './ProductSlider.module.scss'

export type ProductPicker = {
  pIMID: number
}

export type ProductSliderProps = {
  productPicker: ProductPicker[]
  products?: ProductGridTileFragment[]
  productsPerView: string
  controls: boolean
  spaceBetween?: number
  centeredSlides?: boolean
  recommendation?: boolean
  recommendationServingConfig?: ServingConfig
  recommendationEventType?: EventType
  recommendationSize?: number
  recommendationsMixProducts?: boolean
  tileColor?: 'dark' | 'light'
  fontColor?: string
  backgroundColor?: string
}
const ProductSlider: FC<ProductSliderProps> = ({
  products,
  controls,
  productsPerView,
  spaceBetween,
  centeredSlides,
  recommendation,
  recommendationServingConfig,
  recommendationEventType,
  recommendationSize,
  recommendationsMixProducts,
  tileColor = 'light',
  fontColor,
  backgroundColor,
}) => {
  if (!productsPerView || productsPerView === '') {
    productsPerView = '4.5'
  }
  if (!spaceBetween || spaceBetween === 0) {
    spaceBetween = 50
  }

  const tabletSlidesPerView = 2.5
  const mobileSlidesPerView = 1.5

  const [prevEl, setPrevEl] = useState<HTMLElement | null>(null)
  const [nextEl, setNextEl] = useState<HTMLElement | null>(null)

  const [recommendations, { loading }] = useRecommendations({
    mixProducts: recommendationsMixProducts,
    fetchRecommendations: recommendation,
    servingConfig: recommendationServingConfig,
    eventType: recommendationEventType,
    pageSize: recommendationSize,
    products: products,
  })

  const insufficientSlides =
    (recommendation ? recommendationSize || 10 : products?.length) <
    (isMobile() && !isTablet()
      ? isTablet()
        ? tabletSlidesPerView
        : mobileSlidesPerView
      : parseFloat(productsPerView))

  const [isFirstSlide, setIsFirstSlide] = useState(true)
  const [isLastSlide, setIsLastSlide] = useState(false)

  return (
    <div
      className={s.root}
      style={
        fontColor || backgroundColor
          ? ({
              '--font-color': fontColor || undefined,
              '--background-color': backgroundColor || undefined,
            } as CSSProperties)
          : undefined
      }
      data-module="ProductSlider"
    >
      {controls && (
        <div className="flex justify-end mr-4 md:mr-24 cursor-default mb-4">
          <button
            ref={(node) => setPrevEl(node)}
            className={clsx(s.swiperButton, { [s.disabled]: isFirstSlide })}
          >
            <ChevronBackCircleOutline size={35} />
          </button>
          <button
            ref={(node) => setNextEl(node)}
            className={clsx(s.swiperButton, { [s.disabled]: isLastSlide })}
          >
            <ChevronForwardCircleOutline size={35} />
          </button>
        </div>
      )}
      <Swiper
        className={clsx(s.swiperWrapper, {
          // [s.centeredSlides]: insufficientSlides && centeredSlides,
        })}
        slidesPerView={1.5}
        slidesPerGroup={1}
        spaceBetween={30}
        slidesOffsetAfter={insufficientSlides && centeredSlides ? 0 : 30}
        slidesOffsetBefore={insufficientSlides && centeredSlides ? 0 : 30}
        modules={[Keyboard, Mousewheel, FreeMode, Navigation]}
        navigation={{ prevEl, nextEl }}
        onSlideChange={(swiper) => {
          swiper.currentBreakpoint >= 748
            ? setIsFirstSlide(swiper.activeIndex === 0)
            : setIsFirstSlide(swiper.activeIndex === 0)
          swiper.currentBreakpoint >= 748
            ? setIsLastSlide(
                swiper.activeIndex + parseInt(productsPerView) ===
                  swiper.slides.length,
              )
            : setIsLastSlide(swiper.activeIndex === swiper.slides.length - 1)
        }}
        keyboard
        mousewheel={{ forceToAxis: true }}
        cssMode={true}
        freeMode={false}
        grabCursor
        enabled={products?.length > 1}
        centeredSlides={insufficientSlides && centeredSlides}
        centerInsufficientSlides={insufficientSlides && centeredSlides}
        centeredSlidesBounds={insufficientSlides && centeredSlides}
        touchEventsTarget="container"
        breakpoints={{
          748: {
            slidesPerGroup: 2,
            slidesPerView: 2.5,
            freeMode: {
              enabled: true,
              sticky: false,
            },
            cssMode: false,
            spaceBetween: spaceBetween,
            slidesOffsetAfter:
              insufficientSlides && centeredSlides ? 0 : spaceBetween,
            slidesOffsetBefore:
              insufficientSlides && centeredSlides ? 0 : spaceBetween,
            enabled: !insufficientSlides,
          },
          1048: {
            freeMode: {
              enabled: true,
              sticky: false,
            },
            cssMode: false,
            slidesPerView: parseFloat(productsPerView),
            slidesPerGroup: parseInt(productsPerView),
            spaceBetween: spaceBetween,
            slidesOffsetAfter:
              insufficientSlides && centeredSlides ? 0 : spaceBetween,
            slidesOffsetBefore:
              insufficientSlides && centeredSlides ? 0 : spaceBetween,
            enabled: !insufficientSlides,
          },
        }}
      >
        {recommendation && recommendations
          ? recommendations
              .slice(0, recommendationSize || 10)
              .map((product, index) => (
                <SwiperSlide key={index}>
                  <ProductCard tileColor={tileColor} product={product} />
                </SwiperSlide>
              ))
          : products?.map((product, index) => (
              <SwiperSlide key={index}>
                <ProductCard tileColor={tileColor} product={product} />
              </SwiperSlide>
            ))}
      </Swiper>
    </div>
  )
}

export default ProductSlider
